import { Inject, Injectable } from '@angular/core';
import { PILocalData, PiScheduleDetails, PIScheduleLocalData } from '../class/pi-schedule';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ConcurrentUsageService, isDefined, LEGACY_CONCURRENT_USAGE_KEYS } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { JsonPhysicalInventoryModel, PhysicalInventoryModel } from '../models/physical-inventory.model';

@Injectable({
    providedIn: 'root',
})
export class PhysicalInventoryStorageService {
    public readonly PI_SCHEDULE_USAGE_KEY = LEGACY_CONCURRENT_USAGE_KEYS.physicalInventorySchedule;
    public readonly PI_DATA_USAGE_KEY = LEGACY_CONCURRENT_USAGE_KEYS.physicalInventory;

    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    private serialize(details: PiScheduleDetails): PIScheduleLocalData['value'] {
        return {
            isScheduleAlertConfirmed: details.isScheduleAlertConfirmed,
            isPISubmitted: details.isPISubmitted,
            startDateAndTime: details.startDateAndTime.clone().toISOString(),
            endDateAndTime: details.endDateAndTime.clone().toISOString(),
            isOfflineAlertConfirmed: details.isOfflineAlertConfirmed,
            isRescheduleAlertConfirmed: details.isRescheduleAlertConfirmed,
            isNewScheduleAlertConfirmed: details.isNewScheduleAlertConfirmed,
            isRescheduled: details.isRescheduled,
        };
    }

    private deserialize(serialized: PIScheduleLocalData): PiScheduleDetails {
        return {
            isScheduleAlertConfirmed: serialized.value.isScheduleAlertConfirmed,
            isPISubmitted: serialized.value.isPISubmitted,
            startDateAndTime: moment(serialized.value.startDateAndTime).utc(),
            endDateAndTime: moment(serialized.value.endDateAndTime).utc(),
            isOfflineAlertConfirmed: serialized.value.isOfflineAlertConfirmed,
            isRescheduleAlertConfirmed: serialized.value.isRescheduleAlertConfirmed,
            isNewScheduleAlertConfirmed: serialized.value.isNewScheduleAlertConfirmed,
            isRescheduled: serialized.value.isRescheduled,
        };
    }

    public savePIScheduleDetails(value: PiScheduleDetails): Observable<PiScheduleDetails> {
        const item: PIScheduleLocalData = {
            key: this.PI_SCHEDULE_USAGE_KEY,
            value: this.serialize(value),
            updatedAt: Date.now(),
        };

        this.log.debug(
            `${this.constructor.name}: savePISheduleDetails: saving physical inventory schedule data to storage`,
        );

        return this.concurrentUsageService
            .writeDataForKey(this.PI_SCHEDULE_USAGE_KEY, JSON.stringify(item), item.updatedAt)
            .pipe(map(() => this.deserialize(item)))
            .pipe(
                catchError(() => {
                    this.log.warn(`${this.constructor.name}: could not save to storage`);
                    return of(value);
                }),
            );
    }

    public getPIScheduleDetails(): Observable<PiScheduleDetails | undefined> {
        return this.concurrentUsageService.readDataForKey(this.PI_SCHEDULE_USAGE_KEY).pipe(
            catchError((err) => {
                this.log.error(
                    `PhysicalInventoryStorageService: getPIScheduleDetails: an error occurred during IndexedDb read for ${this.PI_SCHEDULE_USAGE_KEY}: ${err}`,
                );
                return of(undefined);
            }),
            map((data) => {
                const dataValue = data?.[0]?.value;
                if (!dataValue) {
                    return undefined;
                }
                return JSON.parse(dataValue);
            }),
            map((data) => (data ? this.deserialize(data) : undefined)),
        );
    }

    public deletePIScheduleDetails(): Observable<undefined> {
        return this.concurrentUsageService.deleteDataForKey(this.PI_SCHEDULE_USAGE_KEY).pipe(
            // The return type of deleteDataForKey is Observable<unknown>.
            // It is mapped to Observable<void> to avoid handling multiple return types, which could be error-prone
            map(() => undefined),
        );
    }

    private savePIDataToStorage(data: PILocalData): Observable<JsonPhysicalInventoryModel> {
        return this.concurrentUsageService
            .writeDataForKey(this.PI_DATA_USAGE_KEY, JSON.stringify(data), data.updatedAt)
            .pipe(map(() => JSON.parse(data.value)));
    }

    private getPIDataFromStorage(): Observable<PILocalData | undefined> {
        return this.concurrentUsageService.readDataForKey(this.PI_DATA_USAGE_KEY).pipe(
            map((data) => {
                const dataValue = data?.[0]?.value;
                if (!dataValue) {
                    return undefined;
                }
                return JSON.parse(dataValue);
            }),
        );
    }

    public savePIData(physicalInventoryModel: PhysicalInventoryModel): Observable<PhysicalInventoryModel> {
        const item: PILocalData = {
            key: this.PI_DATA_USAGE_KEY,
            value: JSON.stringify(physicalInventoryModel),
            updatedAt: Date.now(),
        };

        this.log.debug(`${this.constructor.name}: savePIData: saving physical inventory schedule data storage`);

        return this.savePIDataToStorage(item).pipe(
            map(() => physicalInventoryModel),
            catchError(() => {
                this.log.warn(`${this.constructor.name}: savePIData: could not save to storage`);
                return of(physicalInventoryModel);
            }),
        );
    }

    public getPIData(): Observable<JsonPhysicalInventoryModel | undefined> {
        return this.getPIDataFromStorage().pipe(
            catchError((error) => {
                this.log.warn(`${this.constructor.name}: getPIData: could not read PI Data from storage`, error);
                return of(undefined);
            }),
            map((piData) => {
                this.log.debug(`${this.constructor.name}: getting physical inventory data from storage`);

                if (!isDefined(piData)) {
                    return undefined;
                }

                return JSON.parse(piData.value);
            }),
        );
    }

    public deletePIData(): Observable<undefined> {
        return this.concurrentUsageService.deleteDataForKey(this.PI_DATA_USAGE_KEY).pipe(
            // The return type of deleteDataForKey is Observable<unknown>.
            // It is mapped to Observable<void> to avoid handling multiple return types, which could be error-prone
            map(() => undefined),
        );
    }
}
